import axios from 'axios'
import { authService } from '../services/auth/authService'
import { getEnvironmentConfig } from '../utils/detectEnv'
import { notifyTortTemporarilyUnavailable } from '../utils/globalApiStatus'
import endpoints from './endpoints'

// Get baseURL from environment config
const envConfig = getEnvironmentConfig()
const baseURL = envConfig.apiBaseUrl

console.log('Axios baseURL configured:', baseURL)

const api = axios.create({
  baseURL: baseURL,
})

// Shared state to prevent concurrent token refresh attempts
let isRefreshing = false
let refreshSubscribers: Array<(token: string | null) => void> = []

function subscribeTokenRefresh(cb: (token: string | null) => void) {
  refreshSubscribers.push(cb)
}

function onTokenRefreshed(token: string | null) {
  refreshSubscribers.forEach((cb) => cb(token))
  refreshSubscribers = []
}

// A token this close to expiry is refreshed pre-emptively instead of being
// sent — sending it would guarantee a 401, doubling backend traffic during
// the token-expiry window (send-fail-401, refresh, retry).
const TOKEN_REFRESH_BUFFER_MS = 30_000

/**
 * Returns a usable access token, refreshing it first if missing, expired, or
 * within TOKEN_REFRESH_BUFFER_MS of expiring. Concurrent callers (the request
 * interceptor and any in-flight 401 handlers) share one in-flight refresh via
 * the isRefreshing/refreshSubscribers queue.
 * @param force - Skip the expiry check and refresh unconditionally (used when
 * the backend has already rejected the current token with a 401).
 */
async function ensureFreshToken(force = false): Promise<string | null> {
  const currentToken = authService.getAccessToken()

  if (
    !force &&
    currentToken &&
    !authService.isTokenExpiringSoon(currentToken, TOKEN_REFRESH_BUFFER_MS)
  ) {
    return currentToken
  }

  if (isRefreshing) {
    return new Promise((resolve) => subscribeTokenRefresh(resolve))
  }

  isRefreshing = true
  try {
    const refreshed = await authService.refreshAccessToken()
    const newToken = refreshed ? authService.getAccessToken() : null
    onTokenRefreshed(newToken)
    return newToken
  } finally {
    isRefreshing = false
  }
}

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

// Transient backend 5xx/network blips are retried for GET only — retrying a
// non-idempotent POST/PUT/PATCH/DELETE risks duplicate side effects (e.g.
// submitting a time-off request twice).
// A 504 means the gateway already waited for its upstream and gave up.
// Retrying it only adds more backoff while the same upstream remains slow.
const TRANSIENT_RETRY_STATUS_CODES = new Set([502, 503])
const TRANSIENT_RETRY_MAX_ATTEMPTS = 3
const TRANSIENT_RETRY_BACKOFF_MS = [1000, 2000, 4000]

// Matches the 5xx set BACKEND_UNAVAILABLE_MESSAGE (apiError.ts) treats as
// "TORT is down" rather than a request-specific failure.
const BACKEND_UNAVAILABLE_STATUS_CODES = new Set([500, 502, 503, 504])

function isRetryableTransientError(error: any): boolean {
  const method = String(error.config?.method || 'get').toLowerCase()
  if (method !== 'get') return false
  if (!error.response && error.code === 'ERR_NETWORK') return true
  return TRANSIENT_RETRY_STATUS_CODES.has(error.response?.status)
}

function throwNormalizedError(error: any): never {
  if (error instanceof Error) {
    throw error
  }
  const errorMessage = error?.message ?? 'Request failed'
  const errorInstance = new Error(errorMessage) as any
  errorInstance.response = error?.response
  errorInstance.config = error?.config
  errorInstance.request = error?.request
  throw errorInstance
}

api.interceptors.request.use(
  async (config) => {
    // Refresh before sending rather than after a guaranteed 401
    const accessToken = await ensureFreshToken()

    // Set X-nuid and X-emp-nuid headers
    const loggedInNuid = sessionStorage.getItem('userId')?.toUpperCase() ?? ''

    let employeeNuid = loggedInNuid

    // The profile endpoint establishes the AUTHENTICATED session's own
    // identity (name, roles) — it must never be scoped to a Manual Time Off
    // "acting on behalf of" employee, regardless of which code path calls it.
    // Sending X-emp-nuid there would let the backend return the selected
    // employee's profile, silently replacing the logged-in user's identity.
    const isProfileRequest = config.url === endpoints.profile
   const isManualTimeOffProfileRequest =
  (config as any).manualTimeOffProfileRequest === true
    // The selected Manual Time Off employee is only honored while the user is
    // actually on the proxy pages (/admin/requestor, reached exclusively via
    // /admin/manual-timeoff). sessionStorage alone is not enough to gate this:
    // it only clears when the user clicks "Exit Requestor Proxy Manual Mode",
    // so navigating away any other way (nav links, browser back, typing a URL)
    // would otherwise keep every subsequent request scoped to that employee
    // indefinitely. Checking the current location here — evaluated fresh for
    // every request, independent of React/Redux state timing — is the
    // guarantee that a stale selection can never leak into unrelated pages.
    const pathname = globalThis.location?.pathname ?? ''
    const isManualTimeOffProxyRoute =
      pathname.startsWith('/admin/requestor') ||
      pathname.startsWith('/admin/manual-timeoff')

    if ((!isProfileRequest || isManualTimeOffProfileRequest) && isManualTimeOffProxyRoute) {
      try {
        const manualEmployee = sessionStorage.getItem('manualTimeOffEmployee')

        if (manualEmployee) {
          employeeNuid =
            (
              JSON.parse(manualEmployee) as { nuid: string }
            ).nuid?.toUpperCase() ?? loggedInNuid
        }
      } catch {
        employeeNuid = loggedInNuid
      }
    }

    config.headers['X-nuid'] = loggedInNuid
    config.headers['X-emp-nuid'] = employeeNuid

    // Set Authorization header with Bearer token
    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`
      console.log('Authorization header set with token')
    } else {
      config.headers['Authorization'] = ''
      console.log('Authorization header set to empty string (no token)')
    }

    try {
      const base = api.defaults.baseURL || ''
      const relative = String(config.url || '')
      const urlObj = new URL(relative, base)

      // Log original URL
      console.log('Request URL:', urlObj.toString())

      // Always append envlbl=drn or envlbl=dr for specific environments
      if (envConfig.env == 'drn') {
        urlObj.searchParams.append('envlbl', 'drn')
      } else if (envConfig.env == 'dr') {
        urlObj.searchParams.append('envlbl', 'dr')
      }
      console.log('Modified Request URL (envlbl added):', urlObj.toString())

      // Build merged params for axios config.params
      let paramsObj: Record<string, any> = {}

      if (config.params instanceof URLSearchParams) {
        paramsObj = Object.fromEntries(config.params.entries())
      } else if (config.params && typeof config.params === 'object') {
        paramsObj = { ...(config.params as Record<string, any>) }
      }

      // Merge params from the modified URL (these include envlbl plus any that were in the URL)
      const urlParams = Object.fromEntries(urlObj.searchParams.entries())
      paramsObj = { ...paramsObj, ...urlParams }

      config.params = paramsObj

      // Strip any query string from config.url to avoid double-encoding of params by axios
      if (typeof config.url === 'string') {
        const [pathOnly] = relative.split('?')
        config.url = pathOnly
      }
    } catch (e) {
      console.warn('Unable to build request URL for logging', e)
    }

    return config
  },
  (error) => {
    throw error
  }
)

// Response interceptor: retries transient backend errors, refreshes on 401
api.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    const originalRequest = error.config || {}

    if (isRetryableTransientError(error)) {
      const attempt = (originalRequest._retryCount || 0) + 1
      if (attempt <= TRANSIENT_RETRY_MAX_ATTEMPTS) {
        originalRequest._retryCount = attempt
        console.warn(
          `Transient error on ${originalRequest.url} — retrying (attempt ${attempt}/${TRANSIENT_RETRY_MAX_ATTEMPTS})`
        )
        await delay(TRANSIENT_RETRY_BACKOFF_MS[attempt - 1])
        return api(originalRequest)
      }
    }

    // Any backend/gateway failure in this class (500/502/503/504) means TORT
    // itself is unavailable, not just the one request — swap the whole app
    // to the ServiceUnavailable screen instead of letting every section on
    // the page render its own "could not process" banner.
    if (BACKEND_UNAVAILABLE_STATUS_CODES.has(error.response?.status)) {
      notifyTortTemporarilyUnavailable(error.response?.status)
    }

    // ==================== NETWORK ERROR HANDLING ====================
    if (!error.response && error.code === 'ERR_NETWORK') {
      console.error('Network Error:', {
        code: error.code,
        message: error.message,
        url: originalRequest?.url,
      })

      // A network error has no response/status at all, so it never matched
      // BACKEND_UNAVAILABLE_STATUS_CODES above — without this, TORT being
      // completely unreachable (gateway down, connection refused) went
      // unnoticed by the app shell entirely, and every section on the page
      // fell back to rendering its own "technical problem" banner instead of
      // the one global ServiceUnavailable screen.
      notifyTortTemporarilyUnavailable()
      throwNormalizedError(error)
    }

    // If 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        console.log('Token expired - attempting to refresh...')
        const newToken = await ensureFreshToken(true)

        if (newToken) {
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`
          return api(originalRequest)
        }

        // Token refresh failed - redirect to login
        console.log('Token refresh returned false - redirecting to login')
        authService.initiateLogin()
        throwNormalizedError(error)
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError)
        authService.initiateLogin()
        throwNormalizedError(refreshError)
      }
    }

    throwNormalizedError(error)
  }
)

export default api

